import React from "react";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  MaterialRequestStatus,
  MaterialRequestStatusMap,
} from "../constants/material-request.constants";
import { materialRequestSourceStackedCell } from "../utils/material-request-source-link";
import { historyTimestamp, historyMessage } from "utils/status-history";
import { useMaterialRequestOverview } from "./MaterialRequestOverviewContext";
import type { MaterialRequest } from "../types/material-request.types";
import {
  Timeline,
  type TimelineStep,
  type TimelineStepState,
} from "components/common/Timeline";

function buildTimeline(record: MaterialRequest): TimelineStep[] {
  const history = record.statusHistory ?? [];
  if (history.length === 0) {
    const s = record.status as MaterialRequestStatus;
    const label = MaterialRequestStatusMap[s]?.label ?? s;
    const state: TimelineStepState =
      s === MaterialRequestStatus.CANCELLED ||
      s === MaterialRequestStatus.REJECTED
        ? "skipped"
        : s === MaterialRequestStatus.COMPLETED
          ? "done"
          : "active";
    return [{ label, timestamp: record.created_at, state }];
  }
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const s = entry.status as MaterialRequestStatus;
    const label = MaterialRequestStatusMap[s]?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? s === MaterialRequestStatus.CANCELLED ||
        s === MaterialRequestStatus.REJECTED
        ? "skipped"
        : s === MaterialRequestStatus.COMPLETED
          ? "done"
          : "active"
      : "done";
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() || `#${entry.by}`
      : entry.by != null
        ? `#${entry.by}`
        : undefined;
    return {
      label,
      timestamp: entry.at,
      by: byLabel,
      actor: entry.message ?? undefined,
      state,
    };
  });
}

// ─── Main component ────────────────────────────────────────────────────────

const OverviewTab = () => {
  const { materialRequest: record } = useMaterialRequestOverview();

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Request info</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th
                        className="ps-0"
                        scope="row"
                        style={{ width: "180px" }}
                      >
                        Source :
                      </th>
                      <td className="text-muted">
                        {record
                          ? materialRequestSourceStackedCell(record)
                          : "—"}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Required date :
                      </th>
                      <td className="text-muted">
                        {record?.requiredDate ? (
                          <DateOnly date={record.requiredDate} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Notes :
                      </th>
                      <td className="text-muted">
                        {record?.status === MaterialRequestStatus.REJECTED
                          ? historyMessage(record.statusHistory, "REJECTED") ||
                            "—"
                          : record?.notes?.trim() || "—"}
                      </td>
                    </tr>
                    {record?.status === MaterialRequestStatus.REJECTED ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Rejected :
                        </th>
                        <td className="text-muted">
                          {historyTimestamp(
                            record.statusHistory,
                            "REJECTED",
                          ) ? (
                            <DateTime
                              date={
                                historyTimestamp(
                                  record.statusHistory,
                                  "REJECTED",
                                )!
                              }
                            />
                          ) : record?.updated_at ? (
                            <DateTime date={record.updated_at} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ) : record?.status === MaterialRequestStatus.CANCELLED ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Cancelled :
                        </th>
                        <td className="text-muted">
                          {historyTimestamp(
                            record.statusHistory,
                            "CANCELLED",
                          ) ? (
                            <DateTime
                              date={
                                historyTimestamp(
                                  record.statusHistory,
                                  "CANCELLED",
                                )!
                              }
                            />
                          ) : record?.updated_at ? (
                            <DateTime date={record.updated_at} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <th className="ps-0" scope="row">
                          Approved :
                        </th>
                        <td className="text-muted">
                          {record?.approvedAt ? (
                            <DateTime date={record.approvedAt} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {record?.created_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={record.created_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}

                  {record?.updated_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={record.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}

                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status :</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge
                          value={record?.status}
                          map={MaterialRequestStatusMap}
                        />
                      </h5>
                    </div>
                  </Col>

                  {record?.deleted_at != null && record.deleted_at !== "" ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">
                          Deleted
                        </p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={record.deleted_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Col lg={4}>
        <Card>
          <CardBody>
            <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
            {record ? (
              <Timeline steps={buildTimeline(record)} />
            ) : (
              <p className="text-muted small mb-0">No data.</p>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default OverviewTab;
